import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";

const SubscriptionCalculator = () => {
  // State variables for inputs
  const [productPrice, setProductPrice] = useState(5);
  const [plan1Monthly, setPlan1Monthly] = useState(56);
  const [plan1Percentage1, setPlan1Percentage1] = useState(2.9);
  const [plan1Percentage2, setPlan1Percentage2] = useState(1.7);
  const [plan1Fixed, setPlan1Fixed] = useState(0.3);
  const [plan2Percentage, setPlan2Percentage] = useState(10);
  const [plan2Fixed, setPlan2Fixed] = useState(0.5);

  // State variables for calculations
  const [breakEvenTransactions, setBreakEvenTransactions] = useState(0);
  const [breakEvenRevenue, setBreakEvenRevenue] = useState(0);
  const [chartData, setChartData] = useState([]);
  const [tableData, setTableData] = useState([]);

  // Calculate break-even point and generate data
  useEffect(() => {
    // Convert percentages to decimals
    const plan1Percentage1Decimal = plan1Percentage1 / 100;
    const plan1Percentage2Decimal = plan1Percentage2 / 100;
    const plan2PercentageDecimal = plan2Percentage / 100;

    // Calculate fees per transaction
    const plan1FeePerTransaction =
      productPrice * plan1Percentage1Decimal +
      (productPrice * plan1Percentage2Decimal + plan1Fixed);
    const plan2FeePerTransaction =
      productPrice * plan2PercentageDecimal + plan2Fixed;

    // Calculate break-even point
    const feeDifference = plan2FeePerTransaction - plan1FeePerTransaction;

    let breakEvenTx = 0;
    let breakEvenRev = 0;

    if (feeDifference > 0) {
      breakEvenTx = Math.ceil(plan1Monthly / feeDifference);
      breakEvenRev = breakEvenTx * productPrice;
    } else {
      // If Plan 1 fee is higher per transaction, it will never break even
      breakEvenTx = Infinity;
      breakEvenRev = Infinity;
    }

    setBreakEvenTransactions(breakEvenTx);
    setBreakEvenRevenue(breakEvenRev);

    // Generate data for chart and table
    const data = [];
    const table = [];

    // Determine a good max transaction count for the chart
    const maxTransactions =
      breakEvenTx === Infinity ? 300 : Math.max(300, breakEvenTx * 2);

    for (let i = 0; i <= maxTransactions; i += 20) {
      const revenue = i * productPrice;
      const plan1Cost = plan1Monthly + i * plan1FeePerTransaction;
      const plan2Cost = i * plan2FeePerTransaction;
      const betterPlan = plan1Cost < plan2Cost ? "Plan 1" : "Plan 2";

      data.push({
        transactions: i,
        revenue,
        plan1Cost,
        plan2Cost,
      });

      table.push({
        transactions: i,
        revenue,
        plan1Cost,
        plan2Cost,
        betterPlan,
      });
    }

    setChartData(data);
    setTableData(table);
  }, [
    productPrice,
    plan1Monthly,
    plan1Percentage1,
    plan1Percentage2,
    plan1Fixed,
    plan2Percentage,
    plan2Fixed,
  ]);

  // CSS styles
  const styles = {
    container: {
      padding: "20px",
      fontFamily: "Arial, sans-serif",
      maxWidth: "1200px",
      margin: "0 auto",
    },
    heading: {
      fontSize: "24px",
      fontWeight: "bold",
      marginBottom: "20px",
      color: "#333",
    },
    subheading: {
      fontSize: "18px",
      fontWeight: "bold",
      marginBottom: "15px",
      color: "#444",
    },
    sectionHeading: {
      fontSize: "16px",
      fontWeight: "bold",
      marginBottom: "10px",
      color: "#555",
    },
    gridContainer: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
      gap: "20px",
      marginBottom: "25px",
    },
    card: {
      backgroundColor: "#f5f5f5",
      padding: "20px",
      borderRadius: "8px",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    },
    formGroup: {
      marginBottom: "15px",
    },
    label: {
      display: "block",
      marginBottom: "5px",
      fontWeight: "bold",
      fontSize: "14px",
    },
    input: {
      width: "100%",
      padding: "8px",
      border: "1px solid #ccc",
      borderRadius: "4px",
      fontSize: "14px",
    },
    inputGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(100px, 1fr))",
      gap: "10px",
      marginBottom: "15px",
    },
    inputGridSmall: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(80px, 1fr))",
      gap: "10px",
    },
    infoText: {
      fontSize: "12px",
      color: "#666",
      marginTop: "4px",
    },
    resultsGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "15px",
      marginBottom: "15px",
    },
    resultCard: {
      backgroundColor: "white",
      padding: "15px",
      borderRadius: "6px",
      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
    },
    resultLabel: {
      fontSize: "12px",
      color: "#666",
      marginBottom: "5px",
    },
    resultValue: {
      fontSize: "22px",
      fontWeight: "bold",
      color: "#333",
    },
    resultText: {
      fontSize: "14px",
      marginTop: "10px",
      color: "#444",
    },
    chartContainer: {
      height: "300px",
      backgroundColor: "white",
      padding: "20px",
      borderRadius: "8px",
      border: "1px solid #ddd",
      marginBottom: "25px",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
      backgroundColor: "white",
      border: "1px solid #ddd",
      borderRadius: "8px",
      overflow: "auto",
    },
    tableHeader: {
      backgroundColor: "#f0f0f0",
      padding: "10px",
      textAlign: "left",
      borderBottom: "1px solid #ddd",
      fontSize: "14px",
      fontWeight: "bold",
    },
    tableCell: {
      padding: "10px",
      borderBottom: "1px solid #eee",
      fontSize: "14px",
    },
    tableRowEven: {
      backgroundColor: "#f9f9f9",
    },
  };

  return (
    <div style={styles.container}>
      <h1 style={styles.heading}>Subscription Plan Calculator</h1>

      <div style={styles.gridContainer}>
        <div style={styles.card}>
          <h2 style={styles.subheading}>Input Parameters</h2>

          <div style={styles.formGroup}>
            <label style={styles.label}>Product Price ($)</label>
            <input
              type="number"
              value={productPrice}
              onChange={(e) => setProductPrice(parseFloat(e.target.value) || 0)}
              style={styles.input}
              min="0.01"
              step="0.01"
            />
          </div>

          <div style={styles.formGroup}>
            <h3 style={styles.sectionHeading}>
              Plan 1 - Shopify + Stripe (Monthly Fee + Transaction Fees)
            </h3>
            <div style={styles.inputGrid}>
              <div>
                <label style={styles.label}>Monthly Fee ($)</label>
                <input
                  type="number"
                  value={plan1Monthly}
                  onChange={(e) =>
                    setPlan1Monthly(parseFloat(e.target.value) || 0)
                  }
                  style={styles.input}
                  min="0"
                  step="0.01"
                />
              </div>
              <div>
                <label style={styles.label}>Fee 1 (%)</label>
                <input
                  type="number"
                  value={plan1Percentage1}
                  onChange={(e) =>
                    setPlan1Percentage1(parseFloat(e.target.value) || 0)
                  }
                  style={styles.input}
                  min="0"
                  step="0.1"
                />
              </div>
              <div>
                <label style={styles.label}>Fee 2 (%)</label>
                <input
                  type="number"
                  value={plan1Percentage2}
                  onChange={(e) =>
                    setPlan1Percentage2(parseFloat(e.target.value) || 0)
                  }
                  style={styles.input}
                  min="0"
                  step="0.1"
                />
              </div>
              <div>
                <label style={styles.label}>Fixed Fee ($)</label>
                <input
                  type="number"
                  value={plan1Fixed}
                  onChange={(e) =>
                    setPlan1Fixed(parseFloat(e.target.value) || 0)
                  }
                  style={styles.input}
                  min="0"
                  step="0.01"
                />
                <div style={styles.infoText}>Applied with Fee 2</div>
              </div>
            </div>
          </div>

          <div style={styles.formGroup}>
            <h3 style={styles.sectionHeading}>
              Plan 2 - Gumroad (Transaction Fees Only)
            </h3>
            <div style={styles.inputGridSmall}>
              <div>
                <label style={styles.label}>Percentage (%)</label>
                <input
                  type="number"
                  value={plan2Percentage}
                  onChange={(e) =>
                    setPlan2Percentage(parseFloat(e.target.value) || 0)
                  }
                  style={styles.input}
                  min="0"
                  step="0.1"
                />
              </div>
              <div>
                <label style={styles.label}>Fixed Fee ($)</label>
                <input
                  type="number"
                  value={plan2Fixed}
                  onChange={(e) =>
                    setPlan2Fixed(parseFloat(e.target.value) || 0)
                  }
                  style={styles.input}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <h2 style={styles.subheading}>Results</h2>

          <div style={styles.resultsGrid}>
            <div style={styles.resultCard}>
              <p style={styles.resultLabel}>Break-even Transactions</p>
              <p style={styles.resultValue}>
                {breakEvenTransactions === Infinity
                  ? "Never"
                  : breakEvenTransactions}
              </p>
            </div>
            <div style={styles.resultCard}>
              <p style={styles.resultLabel}>Break-even Revenue</p>
              <p style={styles.resultValue}>
                {breakEvenTransactions === Infinity
                  ? "Never"
                  : `$${breakEvenRevenue.toFixed(2)}`}
              </p>
            </div>
          </div>

          <div>
            <p style={styles.resultText}>
              {breakEvenTransactions === Infinity
                ? "Plan 2 is always better with these parameters."
                : `Switch to Plan 1 when you reach ${breakEvenTransactions} transactions per month.`}
            </p>
          </div>
        </div>
      </div>

      <div style={styles.formGroup}>
        <h2 style={styles.subheading}>Cost Comparison Chart</h2>
        <div style={styles.chartContainer}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="transactions"
                label={{
                  value: "Transactions",
                  position: "insideBottomRight",
                  offset: -5,
                }}
              />
              <YAxis
                label={{
                  value: "Cost ($)",
                  angle: -90,
                  position: "insideLeft",
                }}
              />
              <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
              <Legend />
              <Line
                type="monotone"
                dataKey="plan1Cost"
                name="Plan 1 Cost"
                stroke="#8884d8"
                activeDot={{ r: 8 }}
              />
              <Line
                type="monotone"
                dataKey="plan2Cost"
                name="Plan 2 Cost"
                stroke="#82ca9d"
              />
              <Line
                type="monotone"
                dataKey="revenue"
                name="Revenue"
                stroke="#ff7300"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div>
        <h2 style={styles.subheading}>Comparison Table</h2>
        <div style={{ overflowX: "auto" }}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.tableHeader}>Transactions</th>
                <th style={styles.tableHeader}>Revenue</th>
                <th style={styles.tableHeader}>Plan 1 Cost</th>
                <th style={styles.tableHeader}>Plan 2 Cost</th>
                <th style={styles.tableHeader}>Better Plan</th>
              </tr>
            </thead>
            <tbody>
              {tableData.slice(0, 10).map((row, index) => (
                <tr
                  key={index}
                  style={index % 2 === 0 ? styles.tableRowEven : {}}
                >
                  <td style={styles.tableCell}>{row.transactions}</td>
                  <td style={styles.tableCell}>${row.revenue.toFixed(2)}</td>
                  <td style={styles.tableCell}>${row.plan1Cost.toFixed(2)}</td>
                  <td style={styles.tableCell}>${row.plan2Cost.toFixed(2)}</td>
                  <td style={styles.tableCell}>{row.betterPlan}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionCalculator;
